import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { GeocodeResultDc } from "@evergis/api";
import { useMapContext } from "@evergis/react";
import { useTranslation } from "react-i18next";

import { GEOCODE_PROVIDER, geocodeAddress } from "../../../resources/geocode";
import { getGeocodeItems } from "../../../ducks/geocode/selectors";
import { setGeocodeItems } from "../../../ducks/geocode/geocode";
import { addNotification } from "../../../ducks/notifications/notifications";
import { useProjectSrid } from "../../../ducks/project/hooks";
import { fixProjectPosition, getBbox } from "../../../hooks/preview/useGeneratePreview";

export const useGeocode = (providerName: string = GEOCODE_PROVIDER) => {
  const [address, updateGeocodeAddress] = useState("");
  const dispatch = useDispatch();
  const sr = useProjectSrid();
  const geocodeItems = useSelector(getGeocodeItems);
  const { t } = useTranslation("mapControls");
  const { map } = useMapContext();

  const bbox = useMemo(
    () => getBbox(fixProjectPosition(map.position), map.resolution, sr),
    [map.position, map.resolution, sr],
  );

  const { data: geocodeResults, loading } = geocodeAddress.use({
    input: address && { address, sr, bboxPoints: bbox.coordinates, providerName },
    waitInput: true,
  });

  const updateGeocodeItems = useCallback(
    (items: GeocodeResultDc[]) => {
      dispatch(setGeocodeItems(items));
    },
    [dispatch],
  );

  useEffect(() => {
    if (geocodeResults && !loading) {
      if (!geocodeResults.length) {
        dispatch(
          addNotification({
            title: t("addressNotFound"),
            description: t("refineRequest"),
            error: true,
          }),
        );
      }
      updateGeocodeItems(geocodeResults);
    }
  }, [dispatch, geocodeResults, loading, updateGeocodeItems]);

  const clearAddress = useCallback(() => {
    updateGeocodeAddress("");
    updateGeocodeItems([]);
  }, [updateGeocodeItems]);

  return [
    { address, geocodeItems },
    { updateGeocodeItems, updateGeocodeAddress, clearAddress },
  ];
};
